import { useLanguage } from '@api/application-configuration';
import type { CredentialResponse } from '@unistocks/features/authentication/google/google-types';
import { useGoogleClientId } from '@unistocks/features/authentication/google/use-google-client-id';
import { useGoogleScript } from '@unistocks/features/authentication/google/use-google-script';
import { breakpoints } from '@unistocks/shared/breakpoints';
import { Logger } from '@unistocks/shared/services/logger.service';
import { ExternalAuthenticationProvider, type AuthButtonProps } from '@unistocks/types/auth';
import { useCallback, useEffect, useRef } from 'react';
import { getVariableValue } from 'tamagui';

export const GoogleSignin: React.FC<AuthButtonProps> = ({
  type = 'login',
  onLoginComplete,
  onLoginStart,
  onLoginError
}) => {
  const { isLoaded: isScriptLoaded, error: scriptError } = useGoogleScript();
  const buttonRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const padding = getVariableValue('$3', 'space') as number;
  const locale = useLanguage();

  const { data: clientId, isSuccess: isClientIdSuccess } = useGoogleClientId();

  const handleCredentialResponse = useCallback(
    (response: CredentialResponse) => {
      try {
        if (!response.credential) {
          throw new Error('No credential received from Google');
        }

        onLoginComplete({
          provider: ExternalAuthenticationProvider.Google,
          provider_key: '',
          external_token: response.credential
        });
      } catch (error) {
        Logger.error(error, 'Google Sign-In error');
        onLoginError?.(error, ExternalAuthenticationProvider.Google);
      }
    },
    [onLoginComplete, onLoginError]
  );

  const handleButtonClick = useCallback(() => {
    if (!isScriptLoaded || !isClientIdSuccess) return;

    if (!window.google?.accounts) {
      Logger.error('Google Identity Services not loaded');
      onLoginError?.(
        new Error('Google Identity Services not available'),
        ExternalAuthenticationProvider.Google
      );
      return;
    }

    onLoginStart?.(ExternalAuthenticationProvider.Google);
  }, [isClientIdSuccess, isScriptLoaded, onLoginError, onLoginStart]);

  useEffect(() => {
    if (
      !isScriptLoaded ||
      !isClientIdSuccess ||
      !window.google?.accounts ||
      initializedRef.current
    ) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        // TODO: false if being redirected from logout
        auto_select: true,
        use_fedcm_for_prompt: true,
        use_fedcm_for_button: true,
        cancel_on_tap_outside: true,
        ux_mode: 'popup',
        context: type === 'login' ? 'use' : 'signup'
      });

      window.google.accounts.id.renderButton(buttonRef.current as HTMLElement, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: type === 'login' ? 'continue_with' : 'signup_with',
        shape: 'pill',
        locale,
        width: breakpoints.xs,
        click_listener: handleButtonClick
      });

      initializedRef.current = true;

      // Trigger One Tap prompt
      window.google.accounts.id.prompt();

      Logger.debug('Google Identity Services initialized');
    } catch (error) {
      Logger.error(error, 'Failed to initialize Google Identity Services');
      onLoginError?.(error, ExternalAuthenticationProvider.Google);
    }
  }, [
    handleCredentialResponse,
    onLoginError,
    type,
    isScriptLoaded,
    isClientIdSuccess,
    clientId,
    padding,
    locale,
    handleButtonClick
  ]);

  useEffect(() => {
    if (scriptError) {
      Logger.error(scriptError, 'Google script loading error');
      onLoginError?.(scriptError, ExternalAuthenticationProvider.Google);
    }
  }, [scriptError, onLoginError]);

  useEffect(() => {
    return () => {
      if (window.google?.accounts?.id) {
        // Cancel any pending prompts
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  return <div ref={buttonRef} style={{ marginLeft: 'auto', marginRight: 'auto' }} />;
};

GoogleSignin.displayName = 'GoogleSignin';
